/*
 * @Description: 动作面板组件，基于Popup + Picker
 */
'use client';

import { useTranslations } from '@/i18n/use-translations';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import Picker from '../Picker/indexForPrescription';
import Popup from '../Popup';

export interface ActionSheetOption {
  /**
   * 选项标签
   */
  label: string;
  /**
   * 选项值
   */
  value: any;
  /**
   * 是否禁用
   */
  disabled?: boolean;
}

export interface ActionSheetProps {
  /**
   * 是否可见
   */
  open?: boolean;
  /**
   * 面板标题
   */
  title: string;
  /**
   * 标题类名
   */
  titleClassName?: string;
  /**
   * 选项列表
   */
  options: ActionSheetOption[];
  /**
   * 默认选中的值
   */
  defaultValue?: any;
  /**
   * 当前选中的值
   */
  value?: any;
  /**
   * 选项高度
   */
  optionHeight?: number | string;
  /**
   * 可见选项数量
   */
  visibleOptionNum?: number;
  /**
   * 取消按钮文本
   */
  cancelText?: string;
  /**
   * 确认按钮文本
   */
  saveText?: string;
  /**
   * 自定义选项渲染函数
   */
  renderOption?: (option: ActionSheetOption, index: number) => ReactNode;
  /**
   * 选中值变化时的回调
   */
  onChange?: (value: any, option: ActionSheetOption) => void;
  /**
   * 确认按钮点击回调
   */
  onSave?: (value: any, option: ActionSheetOption) => void;
  /**
   * 取消按钮点击回调
   */
  onCancel?: () => void;
  /**
   * 关闭回调
   */
  onClose?: () => void;
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 自定义 z-index 值
   */
  zIndex?: number;
}

const resolveSelectedValue = (
  value: ActionSheetProps['value'],
  defaultValue: ActionSheetProps['defaultValue'],
) => {
  const hasSelectedValue = value !== undefined && value !== null && value !== '';
  return hasSelectedValue ? value : defaultValue;
};

const findSelectedOption = (
  options: ActionSheetOption[],
  value: ActionSheetProps['value'],
  defaultValue: ActionSheetProps['defaultValue'],
) => {
  const selectedValue = resolveSelectedValue(value, defaultValue);
  const selectedOption =
    selectedValue !== undefined
      ? options.find((option) => option.value === selectedValue) ||
        options.find((option) => option.label === selectedValue)
      : undefined;

  return selectedOption ?? options[0] ?? null;
};

const ActionSheet: React.FC<ActionSheetProps> = (props) => {
  const t = useTranslations('actionSheet');
  const {
    open = false,
    title,
    titleClassName = '',
    options = [],
    defaultValue,
    value,
    optionHeight = 44,
    visibleOptionNum = 7,
    cancelText = t('cancelText'),
    saveText = t('saveText'),
    renderOption,
    onChange,
    onSave,
    onCancel,
    onClose,
    className = '',
    zIndex,
  } = props;
  // ios 采用useEffect初始化选中项 会有bug
  const [selectedOption, setSelectedOption] = useState<ActionSheetOption | null>(() => {
    if (defaultValue) {
      return options.find((opt) => opt.value === defaultValue) || null;
    }
    return null;
  });
  const [isDirty, setIsDirty] = useState(false);
  const openCycleRef = useRef(false);

  // 当外部value变化时，更新内部状态
  useEffect(() => {
    const option = findSelectedOption(options, value, defaultValue);
    setSelectedOption(option);
  }, [value, defaultValue, options]);

  // 初始化选中项
  useEffect(() => {
    if (selectedOption === null && options.length > 0) {
      if (!isDirty && defaultValue) {
        const option = options.find((opt) => opt.value === defaultValue) || null;
        setSelectedOption(option);
        return;
      }

      const initialValue = resolveSelectedValue(value, defaultValue);
      const option =
        initialValue !== undefined
          ? options.find((opt) => opt.value === initialValue) || options[0]
          : options[0];

      if (option) {
        setSelectedOption(option);
      }
    }
  }, [options, defaultValue, value, selectedOption, isDirty]);

  const handleChange = (newValue: any, option: ActionSheetOption) => {
    setSelectedOption(option);
    onChange && onChange(newValue, option);
  };

  const handleSave = () => {
    setIsDirty(true);
    if (selectedOption) {
      onSave && onSave(selectedOption.value, selectedOption);
    }
    onClose && onClose();
  };

  const handleCancel = () => {
    const option = findSelectedOption(options, value, defaultValue);
    if (option) {
      setSelectedOption(option);
    }
    onCancel && onCancel();
    onClose && onClose();
  };

  // 自定义标题组件 - 固定样式 (Cancel + Title + Save)
  const customTitle = (
    <div className="h-[11.733vw] bg-white flex items-center justify-between">
      <button onClick={handleCancel} className="text-[#969799] px-[4.267vw] text-[3.733vw]">
        {cancelText}
      </button>
      <div
        className={`font-bb text-[4.533vw] text-primary-text leading-[4.533vw] ${titleClassName}`}
      >
        {title}
      </div>
      <button
        onClick={handleSave}
        className=" px-[4.267vw] text-[3.733vw] font-br text-primary-purple"
      >
        {saveText}
      </button>
    </div>
  );

  useEffect(() => {
    if (!open) {
      openCycleRef.current = false;
      return;
    }

    if (openCycleRef.current || options.length === 0) {
      return;
    }
    openCycleRef.current = true;

    const option = findSelectedOption(options, value, defaultValue);
    if (!option) {
      return;
    }

    setSelectedOption(option);

    // Picker 使用展示 label 定位，保存和回调始终保留原始 value。
    const scrollTimer = window.setTimeout(() => {
      const items = document.querySelectorAll('.current-value-picker');
      items.forEach((item) => {
        if (item.getAttribute('data-value') === String(option.label)) {
          item.scrollIntoView({ block: 'center' });
        }
      });
    }, 0);

    return () => window.clearTimeout(scrollTimer);
  }, [open, options, value, defaultValue]);

  return (
    <Popup
      position="bottom"
      height="400px"
      open={open}
      onClose={handleCancel}
      title={customTitle}
      zIndex={zIndex}
      sx={{
        '& .popup-content': {
          padding: '16px 0px',
        },
      }}
    >
      <div className={`action-sheet ${className}`}>
        {/* defaultValue为什么取的是lable？ */}
        <Picker
          options={options}
          defaultValue={selectedOption?.label}
          optionHeight={optionHeight}
          visibleOptionNum={visibleOptionNum}
          onChange={(_, option) => handleChange(option.value, option)}
          renderOption={renderOption}
        />
      </div>
    </Popup>
  );
};

export default ActionSheet;
